import type { InputHTMLAttributes } from 'react'
import type { FieldDef } from '../domain/types'
import { fieldValidationMessage, formatFieldValue, withHttpScheme } from '../lib/fieldValidation'

// Renderiza um campo de captura a partir da sua FieldDef (schema-driven).
const inputType = (t: FieldDef['type']): string => {
  if (t === 'tel') {return 'tel'}
  if (t === 'email') {return 'email'}
  if (t === 'date') {return 'date'}
  if (t === 'url') {return 'url'}
  return 'text'
}

const inputMode = (t: FieldDef['type']): InputHTMLAttributes<HTMLInputElement>['inputMode'] => {
  if (t === 'tel') {return 'tel'}
  if (t === 'cpf' || t === 'cnpj' || t === 'number') {return 'numeric'}
  return undefined
}

export function FieldRenderer({ def, value, onChange, autoFocus }: {
  def: FieldDef
  value: string
  onChange: (v: string) => void
  autoFocus?: boolean
}) {
  const formattedValue = formatFieldValue(def, value)
  const error = value ? fieldValidationMessage(def, value) : null

  return (
    <div className="fz-field" style={{ margin: 0 }}>
      <label className="fz-field__label">
        {def.label}
        {def.required && <span style={{ color: 'var(--color-danger)' }} aria-hidden> *</span>}
      </label>
      {def.type === 'bool' ? (
        <div className="fz-field__bool" role="radiogroup" aria-label={def.label} aria-required={def.required} aria-invalid={!!error}>
          <button
            type="button"
            className={'fz-field__bool-option ' + (value === 'false' ? 'is-active' : '')}
            onClick={() => onChange('false')}
            autoFocus={autoFocus}
            aria-pressed={value === 'false'}
          >
            Não
          </button>
          <button
            type="button"
            className={'fz-field__bool-option ' + (value === 'true' ? 'is-active' : '')}
            onClick={() => onChange('true')}
            aria-pressed={value === 'true'}
          >
            Sim
          </button>
        </div>
      ) : def.type === 'select' ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={def.label} aria-required={def.required}>
          <option value="" disabled>Selecione…</option>
          {def.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input
          type={inputType(def.type)}
          value={formattedValue}
          onChange={(e) => onChange(formatFieldValue(def, e.target.value))}
          onBlur={def.type === 'url' ? (e) => onChange(withHttpScheme(e.target.value)) : undefined}
          placeholder={def.placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          aria-label={def.label}
          aria-required={def.required}
          aria-invalid={!!error}
          maxLength={def.maxLength ?? (def.type === 'tel' ? 15 : def.type === 'cpf' ? 14 : def.type === 'cnpj' ? 18 : undefined)}
          inputMode={inputMode(def.type)}
        />
      )}
      {error && <div className="fz-field__error">{error}</div>}
    </div>
  )
}
